import { AbilityDisplay } from "../../../../paths/games/cardComponent.tsx";
import { Ability } from "../../../../paths/games/gameboardTypes";
import React, { useRef } from "react";
import { createText } from "../../../../util/cardEffects/cardText.ts";
import { SCHOOL_COLORS } from "../../../../util/cardEffects/effectTypes.ts";
import { useCardScale } from "../../../../util/cardUtils.ts";

interface EffectStackProps {
    effects?: Ability[];
    x: number;
    y: number;
    scale?: number;
}

export const EffectStack: React.FC<EffectStackProps> = ({
    effects,
    x,
    y,
    scale = 1,
}) => {
    const baseScale = useCardScale() * scale;
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    return (
        <div
            style={{
                transformOrigin: `top left`,
                transform: `scale(${baseScale})`,
                position: "absolute",
                left: `${x}px`,
                top: `${y}px`,
                width: `300px`,
                pointerEvents: "none",
                display: "flex",
                flexDirection: "column"
            }}
        >
            {effects?.map((queued, index) => {
                const isTop = index === 0;
                const schoolColor = SCHOOL_COLORS[queued.card.school ?? "Manifest"];

                return (
                    <div
                        key={`${queued.card.id}-${index}`}
                        ref={(el) => {
                            itemRefs.current[index] = el;
                        }}
                        style={{
                            left: 0,
                            zIndex: effects.length - index,
                            backgroundColor: "#374151",
                            borderRadius: `8px 8px 8px 8px`,
                            marginBottom: `2px`
                        }}
                    >
                        {/* Header with card name and school dot */}
                        <div
                            style={{
                                height: `24px`,
                                backgroundColor: "#374151",
                                border: `1px solid ${schoolColor}`,
                                borderRadius: `8px 8px 0 0`,
                                boxShadow: `0 2px 6px ${schoolColor}40`,
                                display: "flex",
                                alignItems: "center",
                                paddingRight: `6px`,
                            }}
                        >
                            {/* School dot */}
                            <div
                                style={{
                                    width: `9px`,
                                    height: `9px`,
                                    borderRadius: "50%",
                                    backgroundColor: schoolColor,
                                    boxShadow: isTop ? `0 0 4px ${schoolColor}` : "none",
                                    flexShrink: 0,
                                    marginLeft: `7px`,
                                    marginRight: `5px`
                                }}
                            />

                            {/* Card name */}
                            <span
                                style={{
                                    color: schoolColor,
                                    fontSize: `17px`,
                                    fontWeight: "bold",
                                    flex: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    textShadow: isTop ? `0 0 5px ${schoolColor}60` : "none",
                                }}
                                title={queued.card.name}
                            >
                                {queued.card.name} {queued.count > 1 && (
                                    ` | ×${queued.count}`
                                )}
                            </span>
                        </div>

                        <div style={{
                            borderLeft: `1px solid ${schoolColor}`,
                            borderRadius: `0 0 8px 8px`,
                        }}>
                        </div>
                        <AbilityDisplay
                            abilityType={queued.source}
                            schoolColor={SCHOOL_COLORS[queued.card.school ?? "Manifest"]}
                            text={createText(queued.effect)}
                            removeBottomMargin={true}
                        />
                    </div>
                );
            })}
        </div>
    );
};